import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import {withRouter} from "react-router-dom";
import routes from "../../../routes";
import RechercheAPI, {
    dernierAppel,
    estTelephone,
    GROUPES,
    lienResultat,
    pagesCorrespondantes,
    resultatsAPlat
} from "../../../util/recherche";
import "./RechercheUniverselle.scss";

const api = new RechercheAPI();
const chercher = dernierAppel(q => api.chercher(q));
const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Recherche universelle, façon Spotlight : Ctrl+K (Cmd+K sur Mac) ou le bouton de l'en-tête, depuis n'importe quelle page.
 * Clients (nom, téléphone quel que soit le format, numéro de série), prises en charge, factures, devis,
 * prestations, produits et pages du menu ; ↑ ↓ pour choisir, Entrée pour ouvrir, Échap pour fermer.
 */
const RechercheUniverselle = inject("rootStore")(observer(({rootStore, history}) => {
    const [ouvert, setOuvert] = useState(false);
    const [texte, setTexte] = useState("");
    const [datas, setDatas] = useState({});
    const [charge, setCharge] = useState(false);
    const [actif, setActif] = useState(0);
    const champ = useRef(null);
    const liste = useRef(null);
    const auth = rootStore.authStore;

    const fermer = useCallback(() => {
        setOuvert(false);
        setTexte("");
        setDatas({});
    }, []);

    // Raccourci clavier global, même quand le curseur est dans un champ.
    useEffect(() => {
        const touche = e => {
            if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOuvert(o => !o);
            }
        };
        window.addEventListener("keydown", touche);
        return () => window.removeEventListener("keydown", touche);
    }, []);

    useEffect(() => {
        if (ouvert) {
            setTimeout(() => champ.current && champ.current.focus(), 0);
        }
    }, [ouvert]);

    // Une recherche 150 ms après la dernière frappe.
    useEffect(() => {
        const q = texte.trim();
        if (q.length < 2) {
            setDatas({});
            setCharge(false);
            return undefined;
        }
        setCharge(true);
        const minuteur = setTimeout(async () => {
            try {
                const reponse = await chercher(q);
                if (reponse !== undefined) {
                    setDatas(reponse);
                    setActif(0);
                    setCharge(false);
                }
            } catch (e) {
                setCharge(false);
            }
        }, 150);
        return () => clearTimeout(minuteur);
    }, [texte]);

    const autorisee = useCallback(route => auth.autorise(route) && (!route.module || auth.modules?.[route.module]), [auth]);
    const groupes = useMemo(() => GROUPES.filter(g => !g.module || auth.modules?.[g.module]), [auth.modules]);
    const pages = useMemo(() => pagesCorrespondantes(routes, texte, autorisee), [texte, autorisee]);
    const resultats = useMemo(() => resultatsAPlat({...datas, pages}, groupes), [datas, pages, groupes]);

    const ouvrir = resultat => {
        const lien = resultat && lienResultat(resultat.groupe, resultat.item);
        if (lien) {
            history.push(lien);
            fermer();
        }
    };

    const clavier = e => {
        if (e.key === "Escape") {
            e.preventDefault();
            fermer();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const pas = e.key === "ArrowDown" ? 1 : -1;
            setActif(a => resultats.length ? (a + pas + resultats.length) % resultats.length : 0);
        } else if (e.key === "Enter") {
            e.preventDefault();
            ouvrir(resultats[actif]);
        }
    };

    // Garde le résultat choisi au clavier visible dans la liste.
    useEffect(() => {
        const element = liste.current?.querySelector(".ru-resultat.actif");
        if (element?.scrollIntoView) {
            element.scrollIntoView({block: "nearest"});
        }
    }, [actif]);

    const q = texte.trim();
    let index = -1;

    return (
        <>
            <button type="button" className="ru-bouton btn btn-outline-primary mr-2" onClick={() => setOuvert(true)}
                    title={"Rechercher partout (" + (MAC ? "⌘K" : "Ctrl+K") + ")"}>
                <i className="fa fa-search" aria-hidden="true"/>
                <span className="ru-bouton-texte d-md-down-none">Rechercher</span>
                <kbd className="d-md-down-none">{MAC ? "⌘K" : "Ctrl K"}</kbd>
            </button>

            {ouvert && (
                <div className="ru-fond" onMouseDown={e => e.target === e.currentTarget && fermer()}>
                    <div className="ru-fenetre" role="dialog" aria-label="Recherche universelle">
                        <div className="ru-champ">
                            <i className={charge ? "fa fa-circle-o-notch fa-spin" : "fa fa-search"} aria-hidden="true"/>
                            <input ref={champ} autoFocus value={texte} onChange={e => setTexte(e.target.value)} onKeyDown={clavier}
                                   placeholder="Client, téléphone, n° de série, facture, prestation, produit, page…"
                                   aria-label="Rechercher" autoComplete="off" spellCheck={false}/>
                            <kbd onClick={fermer}>Échap</kbd>
                        </div>

                        <div className="ru-resultats" ref={liste}>
                            {q.length >= 2 && !charge && resultats.length === 0 && (
                                <div className="ru-vide">
                                    Rien trouvé pour « {q} »{estTelephone(q) ? " : aucun client n'a ce numéro." : "."}
                                </div>
                            )}
                            {q.length < 2 && (
                                <div className="ru-vide">
                                    Tapez un nom, un numéro de téléphone (avec ou sans espaces), un numéro de facture
                                    ou de prise en charge, un code-barres, une prestation ou un produit.
                                </div>
                            )}
                            {groupes.map(groupe => {
                                const items = groupe.cle === "pages" ? pages : (datas[groupe.cle] ?? []);
                                if (!items.length) {
                                    return null;
                                }
                                return (
                                    <div key={groupe.cle} className="ru-groupe">
                                        <div className="ru-groupe-titre">{groupe.titre}</div>
                                        {items.map(item => {
                                            index += 1;
                                            const i = index;
                                            return (
                                                <div key={groupe.cle + item.id}
                                                     className={"ru-resultat" + (i === actif ? " actif" : "")}
                                                     onMouseMove={() => actif !== i && setActif(i)}
                                                     onClick={() => ouvrir({groupe: groupe.cle, item})}>
                                                    <i className={(item.icone ?? groupe.icone) + " ru-icone"} aria-hidden="true"/>
                                                    <div className="ru-texte">
                                                        <div className="ru-titre">{item.titre}</div>
                                                        {item.detail && <div className="ru-detail">{item.detail}</div>}
                                                    </div>
                                                    {item.prix !== undefined && item.prix !== null && (
                                                        <div className="ru-prix">{Number(item.prix).toFixed(2)} €</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="ru-aide">
                            <span><kbd>↑</kbd><kbd>↓</kbd> choisir</span>
                            <span><kbd>Entrée</kbd> ouvrir</span>
                            <span><kbd>{MAC ? "⌘K" : "Ctrl K"}</kbd> ouvrir / fermer</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}));

export default withRouter(RechercheUniverselle);
